import React, { useState, useEffect } from "react";
import { Controller, Control, useWatch } from "react-hook-form";
import { Input, Textarea, Select, SelectItem } from "@heroui/react";
import { IoIosCheckmarkCircle, IoMdAddCircle } from "react-icons/io";
import { MdEditNote } from "react-icons/md";
import AddressModal, { formatAddressDisplay, AddressData } from "../modal/AddressModal";

interface GenericFormFieldProps {
  control: Control<any>;
  classNames: Record<string, string>;
  isReadOnly: boolean | null;
  t: (key: string) => string;
  valueConfidence?: number;
  onFieldChange?: () => void;
  onFieldValidate?: () => void;
  fieldConfig: any;
  initialValue?: string;
  setValue?: (name: string, value: any) => void;
  initialAddressData?: AddressData | null;
  fieldData?: any;
  isFieldValidated?: boolean;
  isModified?: boolean;
}

function GenericFormField({
  control,
  classNames,
  isReadOnly,
  t,
  valueConfidence,
  onFieldChange,
  onFieldValidate,
  fieldConfig,
  initialValue,
  setValue,
  initialAddressData,
  fieldData,
  isFieldValidated = false,
  isModified = false,
}: Readonly<GenericFormFieldProps>) {
  const [hasBeenModified, setHasBeenModified] = useState(isModified);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isAddingOption, setIsAddingOption] = useState(false);
  const [newOptionValue, setNewOptionValue] = useState("");
  const [dynamicChoices, setDynamicChoices] = useState<string[]>([]);

  const [storedAddressData, setStoredAddressData] = useState<AddressData | null>(initialAddressData || null);
  const [addressDisplayValue, setAddressDisplayValue] = useState<string>(
    initialAddressData ? formatAddressDisplay(initialAddressData) : ""
  );

  const watchedAddressDetails = useWatch({
    control,
    name: `${fieldConfig.key}_details`,
    defaultValue: null
  });

  useEffect(() => {
    if (fieldConfig.type === "address" && watchedAddressDetails?.valueDetail) {
      setStoredAddressData(watchedAddressDetails.valueDetail);
      if (watchedAddressDetails.label) {
        setAddressDisplayValue(watchedAddressDetails.label);
      }
    }
  }, [watchedAddressDetails, fieldConfig.type]);

  useEffect(() => {
    setHasBeenModified(isModified);
  }, [isModified]);

  const getConfidenceBorderClass = (isMandatory: boolean, confidence?: number): string => {
    if (!isMandatory || hasBeenModified || confidence === undefined || isFieldValidated) {
      return "";
    }

    if (confidence < 50) {
      return "border-red-500 border-2";
    }
    if (confidence >= 50 && confidence < 75) {
      return "border-orange-300 border-2";
    }
    return "";
  };

  const renderField = (fieldConfig: any, field: any) => {
    const { title, type, isMandatory } = fieldConfig;
    const hasError = isMandatory && !field.value;
    const confidenceBorderClass = getConfidenceBorderClass(isMandatory, valueConfidence);
    const errorBorderClass = hasError ? "border-red-500 border-2" : "";
    const baseClassNames = classNames.inputWrapper || "";
    const combinedClassNames =
      `${baseClassNames} ${confidenceBorderClass} ${errorBorderClass}`.trim();

    const handleChange = (event: any) => {
      field.onChange(event);
      if (onFieldChange) {
        onFieldChange();
      }
      if (onFieldValidate) {
        onFieldValidate();
      }
      if (!hasBeenModified) {
        setHasBeenModified(true);
      }
    };

    switch (type) {
      case "string":
        return (
          <Input
            {...field}
            type="text"
            label={t(title)}
            placeholder={t(title)}
            variant="flat"
            labelPlacement="outside"
            classNames={{ ...classNames, inputWrapper: combinedClassNames }}
            isReadOnly={isReadOnly}
            onChange={handleChange}
          />
        );
      case "date":
        return (
          <Input
            {...field}
            type="date"
            label={t(title)}
            placeholder={t(title)}
            variant="flat"
            labelPlacement="outside"
            classNames={{ ...classNames, inputWrapper: combinedClassNames }}
            isReadOnly={isReadOnly}
            onChange={handleChange}
          />
        );
      case "price":
        return (
          <Input
            {...field}
            type="number"
            label={t(title)}
            placeholder={t(title)}
            variant="flat"
            labelPlacement="outside"
            classNames={{ ...classNames, inputWrapper: combinedClassNames }}
            isReadOnly={isReadOnly}
            onChange={handleChange}
            endContent={
              <div className="pointer-events-none flex items-center">
                <span className="text-default-400 text-small">€</span>
              </div>
            }
          />
        );
      case "select": {
        const baseChoices = fieldConfig.choice === "dynamic"
          ? (fieldData?.choice || [])
          : (fieldConfig.choice || []);

        let allChoices = fieldConfig.choice === "dynamic"
          ? [...baseChoices, ...dynamicChoices]
          : baseChoices;

        if (fieldConfig.choice === "dynamic" && field.value && !allChoices.includes(field.value)) {
          allChoices = [field.value, ...allChoices];
        }

        const selectChoices = fieldConfig.choice === "dynamic"
          ? ["", ...allChoices]
          : allChoices;

        const selectedValue = field.value || "";

        const getOptionLabel = (option: string): string => {
          if (option === "") {
            return t("unavailable");
          }
          return fieldConfig.choice === "dynamic" ? option : t(option);
        };

        const handleAddOption = () => {
          if (newOptionValue.trim()) {
            setDynamicChoices([...dynamicChoices, newOptionValue.trim()]);
            field.onChange(newOptionValue.trim());
            if (onFieldChange) {
              onFieldChange();
            }
            if (onFieldValidate) {
              onFieldValidate();
            }
            if (!hasBeenModified) {
              setHasBeenModified(true);
            }
            setNewOptionValue("");
            setIsAddingOption(false);
          }
        };

        return (
          <>
            {isAddingOption ? (
              <Input
                type="text"
                label={t(title)}
                placeholder={t("enter_new_option")}
                variant="flat"
                labelPlacement="outside"
                classNames={classNames}
                value={newOptionValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewOptionValue(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddOption();
                  } else if (e.key === "Escape") {
                    setIsAddingOption(false);
                    setNewOptionValue("");
                  }
                }}
                onBlur={handleAddOption}
                autoFocus
              />
            ) : (
              <Select
                {...field}
                label={t(title)}
                classNames={{ ...classNames, trigger: combinedClassNames }}
                placeholder={t("select")}
                selectedKeys={[selectedValue]}
                labelPlacement="outside"
                disableSelectorIconRotation
                disallowEmptySelection
                onChange={handleChange}
              >
                {selectChoices.map((option: string) => (
                  <SelectItem key={option}>
                    {getOptionLabel(option)}
                  </SelectItem>
                ))}
              </Select>
            )}
          </>
        );
      }
      case "address": {
        const addressObject = storedAddressData || watchedAddressDetails?.valueDetail || field.value;

        const currentDisplayValue = addressDisplayValue ||
          (typeof field.value === 'string' ? field.value : formatAddressDisplay(field.value));

        return (
          <>
            <Input
              name={field.name}
              value={currentDisplayValue}
              type="text"
              label={t(title)}
              placeholder={t(title)}
              variant="flat"
              labelPlacement="outside"
              classNames={{ ...classNames, inputWrapper: combinedClassNames }}
              isReadOnly={true}
              onClick={() => !isReadOnly && setIsAddressModalOpen(true)}
              className={isReadOnly ? "" : "cursor-pointer"}
              onChange={() => {}}
            />
            <AddressModal
              isOpen={isAddressModalOpen}
              onClose={() => setIsAddressModalOpen(false)}
              initialData={addressObject}
              classNames={classNames}
              onSubmit={(data) => {
                const formattedLabel = formatAddressDisplay(data);

                setStoredAddressData(data);
                setAddressDisplayValue(formattedLabel);

                // Met à jour React Hook Form
                field.onChange(formattedLabel);

                if (setValue) {
                  setValue(`${fieldConfig.key}_details`, {
                    value: formattedLabel,
                    label: formattedLabel,
                    valueDetail: data
                  });
                }

                if (onFieldChange) {
                  onFieldChange();
                }
                if (!hasBeenModified) {
                  setHasBeenModified(true);
                }
              }}
            />
          </>
        );
      }
      default:
        return (
          <Textarea
            {...field}
            label={t(title)}
            placeholder={t(title)}
            variant="flat"
            labelPlacement="outside"
            classNames={{ ...classNames, inputWrapper: combinedClassNames }}
            isReadOnly={isReadOnly}
            onChange={handleChange}
          />
        );
    }
  };

  return (
    <div className="flex items-end" key={fieldConfig.key}>
      <Controller
        name={fieldConfig.key}
        control={control}
        defaultValue={initialValue ?? (fieldConfig.type === "address" ? null : "")}
        render={({ field }) => renderField(fieldConfig, field)}
      />
      {fieldConfig.type === "address" && !isReadOnly && (
        <button
          className="flex items-center p-2 rounded-full text-white bg-vistagreen/10 cursor-pointer mx-1"
          onClick={(event) => {
            event.preventDefault();
            setIsAddressModalOpen(true);
          }}
        >
          <MdEditNote className="fill-vistagreen/70 h-6 w-6" />
          <span className="w-0 group-hover/validate:w-auto group-hover/validate:px-2 group-hover/validate:flex hidden">
            {t("edit")}
          </span>
        </button>
      )}
      {fieldConfig.type === "select" && fieldConfig.choice === "dynamic" && !isReadOnly && (
        <button
          className="flex items-center p-2 rounded-full text-white bg-vistagreen/10 cursor-pointer mx-1"
          onClick={(event) => {
            event.preventDefault();
            setIsAddingOption(true);
          }}
        >
          <IoMdAddCircle className="fill-vistagreen/70 h-6 w-6" />
          <span className="w-0 group-hover/validate:w-auto group-hover/validate:px-2 group-hover/validate:flex hidden">
            {t("add_option")}
          </span>
        </button>
      )}
      {fieldConfig.isMandatory && valueConfidence !== undefined && valueConfidence < 75 && !isFieldValidated && (
        <button
          className="flex items-center p-2 rounded-full text-white bg-vistagreen/10 cursor-pointer mx-1"
          onClick={(event) => {
            event.preventDefault();
            if (onFieldValidate) {
              onFieldValidate();
            }
            setHasBeenModified(true);
          }}
        >
          <IoIosCheckmarkCircle className="fill-vistagreen/70 h-6 w-6" />
          <span className="w-0 group-hover/validate:w-auto group-hover/validate:px-2 group-hover/validate:flex hidden">
            {t("validate")}
          </span>
        </button>
      )}
    </div>
  );
};

export default GenericFormField;
